import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import Tooltip from '@mui/material/Tooltip';
import Popper from '@mui/material/Popper';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCog, faSync, faUser, faUserNinja, faGavel, faInfoCircle, faTimes, faRibbon, faUndo, faFileDownload, faCheckCircle, faCircle } from '@fortawesome/free-solid-svg-icons';
import { faStar as farStar, faUser as farUser } from '@fortawesome/free-regular-svg-icons';
import { faPatreon, faTwitch } from '@fortawesome/free-brands-svg-icons';
import { constants } from 'dataforazeroth-common';

import FavoriteButton from '../components/FavoriteButton';
import NumberInput from '../components/NumberInput';
import Section from '../components/Section';
import CharactersError from './CharactersError';
import GuildModal from './GuildModal';
import MoveButton from './MoveButton';
import RefreshButton from '../components/RefreshButton';
import MainButton from './MainButton';
import HideButton from './HideButton';
import ToggleButton from '../components/ToggleButton';
import ToggleButtonWrapper from '../collections/ToggleButtonWrapper';
import SortButton from '../components/SortButton';
import CollapseButton from '../components/CollapseButton';
import FilterButton from '../components/FilterButton';
import LanguageDown from '../components/LanguageDown';
import BlockUpdateButton from './BlockUpdateButton';
import RenderIfVisible from '../components/RenderIfVisible';

import favorite from '../util/favorite';
import api from '../util/api';
import utils from '../util/utils';
import login from '../util/login';
import download from '../util/download';

import './Team.scss';

const accountName = (id) => {
    const rename = localStorage.getItem('rename.' + id);
    return rename || ('Account #' + (id || 'Unknown'));
}

// these drop-downs use different name for Unknown
const RACES = constants.races.map(r => r.key === '' ? { key: r.key, value: 'Unknown' } : r);
const CLASSES = utils.classes.map(c => c.key === '' ? { key: c.key, value: 'Unknown' } : c);

const PROFESSIONS = {};
utils.professions.forEach(p => PROFESSIONS[p.toLowerCase()] = p);
const PRIMARIES = { ...PROFESSIONS };
[ 'archaeology', 'cooking', 'fishing', 'gnomish engineering', 'goblin engineering' ].forEach(p => delete PRIMARIES[p]);
const PROFFILTERS = [ { key: '', value: 'Unknown' }, ...Object.keys(PRIMARIES).map(key => ({ key, value: PRIMARIES[key] })) ];

const RACESMAP = {};
constants.races.forEach(r => RACESMAP[r.key] = r.value);

const CHARSORTS = {
    'realm-asc': (a, b) => utils.collator.compare(a.realm, b.realm),
    'realm-desc': (a, b) => utils.collator.compare(b.realm, a.realm),
    'name-asc': (a, b) => utils.collator.compare(a.character, b.character),
    'name-desc': (a, b) => utils.collator.compare(b.character, a.character),
    'guild-asc': (a, b) => utils.collator.compare(a.details?.character?.guildName || '', b.details?.character?.guildName || ''),
    'guild-desc': (a, b) => utils.collator.compare(b.details?.character?.guildName || '', a.details?.character?.guildName || ''),
    'level-asc': (a, b) => 10000 * (a.details?.character?.level || 0) + (a.details?.character?.averageItemLevel || 0) - 10000 * (b.details?.character?.level || 0) - (b.details?.character?.averageItemLevel || 0),
    'level-desc': (a, b) => 10000 * (b.details?.character?.level || 0) +  (b.details?.character?.averageItemLevel || 0) - 10000 * (a.details?.character?.level || 0) - (a.details?.character?.averageItemLevel || 0),
    'wowaccount-asc': (a, b) => utils.collator.compare(accountName(a?.details?.wowaccount), accountName(b?.details?.wowaccount)),
    'wowaccount-desc': (a, b) => utils.collator.compare(accountName(b?.details?.wowaccount), accountName(a?.details?.wowaccount))
};

const SORTNAMES = {
    'manual': 'Manual',
    'realm': 'Realm',
    'guild': 'Guild',
    'name': 'Name',
    'level': 'Level',
    'wowaccount': 'Account'
};

const FILTERS = {
    // search bar
    active: {
        label: 'Active',
        include: state => !state.showActive,
        eval: (state, item) => {
            // active = not hidden, has details, does not have an error message
            if (!item.hidden && item.details && !item.details.message) return false;
            return true;
        }
    },
    missing: {
        label: 'Missing',
        include: state => !state.showNotFound,
        eval: (state, item) => {
            // missing = not hidden, no details OR error message
            if (!item.hidden && (!item.details || item.details.message)) return false;
            return true;
        }
    },
    hidden: {
        label: 'Hidden',
        include: state => !state.showHidden,
        eval: (state, item) => !item.hidden
    },

    // filters
    factions: {
        label: 'Faction',
        include: state => state.hideFactions.size,
        eval: (state, item) => !state.hideFactions.has(item.details?.character?.faction === undefined ? '' : item.details?.character?.faction)
    },
    races: {
        label: 'Race',
        include: state => state.hideRaces.size,
        eval: (state, item) => !state.hideRaces.has(item.details?.character?.race === undefined ? '' : item.details?.character?.race)
    },
    classes: {
        label: 'Class',
        include: state => state.hideClasses.size,
        eval: (state, item) => !state.hideClasses.has(item.details?.character?.class === undefined ? '' : item.details?.character?.class)
    },
    professions: {
        label: 'Profession',
        include: state => state.hideProfessions.size,
        eval: (state, item) => {
            // only hide if BOTH professions are hidden, but be careful with characters that don't have 2
            const professions = Object.keys(item.details?.character?.professions || {}).filter(p => PRIMARIES[p]);
            return professions.some(p => !state.hideProfessions.has(p));
        }
    },
    levels: {
        label: 'Level',
        include: state => state.levelMin || state.levelMax,
        eval: (state, item) => {
            const level = item.details?.character?.level;
            if (!level) return false;

            const min = state.levelMin ? parseInt(state.levelMin) : -Infinity;
            const max = state.levelMax ? parseInt(state.levelMax) : Infinity;
            return level >= min && level <= max;
        }
    },
    ilvls: {
        label: 'Item Level',
        include: state => state.itemLevelMin || state.itemLevelMax,
        eval: (state, item) => {
            const ilvl = item.details?.character?.averageItemLevel;
            if (!ilvl) return false;

            const min = state.itemLevelMin ? parseInt(state.itemLevelMin) : -Infinity;
            const max = state.itemLevelMax ? parseInt(state.itemLevelMax) : Infinity;
            return ilvl >= min && ilvl <= max;
        }
    },
    realms: {
        label: 'Realm',
        include: state => state.hideRealms.size,
        eval: (state, item) => !state.hideRealms.has(item.realm || 0)
    },
    accounts: {
        label: 'Account',
        include: state => state.hideAccounts.size,
        eval: (state, item) => !state.hideAccounts.has(item.details?.wowaccount || 0)
    },

    // expensive filters last
    keywords: {
        include: state => state.regex,
        eval: (state, item) => item.fulltext?.match(state.regex)
    }
};

export default class Team extends Component {
    constructor(props) {
        super(props);
        this.blankstate = {
            loadingAll: false,
            loadingOne: false,
            loadingExclusive: false,
            error: false,
            favorites: null,
            favkey: 'favorites',
            realms: null,
            wowaccounts: null,
            filter: '',
            levelMin: localStorage.rememberFilters === 'true' ? localStorage.getItem('favfilter.levelMin') || '' : '',
            levelMax: localStorage.rememberFilters === 'true' ? localStorage.getItem('favfilter.levelMax') || '' : '',
            itemLevelMin: localStorage.rememberFilters === 'true' ? localStorage.getItem('favfilter.itemLevelMin') || '' : '',
            itemLevelMax: localStorage.rememberFilters === 'true' ? localStorage.getItem('favfilter.itemLevelMax') || '' : '',
            regex: null,
            sort: localStorage.getItem('favsort') || 'manual',
            main: 0,
            character: null,
            account: null,
            details: null,
            exclusive: null,
            showActive: localStorage.showActive !== 'false',
            showNotFound: localStorage.showNotFound !== 'false',
            showHidden: false, // localStorage.showHidden === 'true',
            showFactions: this.initDrawer('showFactions'),
            showRaces: this.initDrawer('showRaces'),
            showClasses: this.initDrawer('showClasses'),
            showProfessions: this.initDrawer('showProfessions'),
            showLevels: this.initDrawer('showLevels'),
            showItemLevels: this.initDrawer('showItemLevels'),
            showRealms: this.initDrawer('showRealms'),
            showAccounts: this.initDrawer('showAccounts'),
            showFilters: false,
            showSort: false,
            hideFactions: this.initSet('favfilter.hideFactions'),
            hideRaces: this.initSet('favfilter.hideRaces'),
            hideClasses: this.initSet('favfilter.hideClasses'),
            hideProfessions: this.initSet('favfilter.hideProfessions'),
            hideRealms: this.initSet('favfilter.hideRealms'),
            hideAccounts: this.initSet('favfilter.hideAccounts'),
            sortedCharacters: [],
            filteredCharacters: [],
            guildModal: false
        };
        this.state = { ...this.blankstate };
    }

    componentDidMount() {
        this.loadAll();
    }

    componentDidUpdate(previous) {
        if (this.props.from === previous.from) return;
        this.loadAll();
    }

    initDrawer(name) {
        const remember = localStorage.rememberFilters === 'true';
        if (!remember) return false;

        return localStorage.getItem('favdrawer.' + name) === 'true';
    }

    modifyDrawer(name) {
        // toggle the value
        if (this.state[name]) {
            localStorage.removeItem('favdrawer.' + name);
            this.setState({ [name]: false });
        } else {
            localStorage.setItem('favdrawer.' + name, 'true');
            this.setState({ [name]: true });
        }
    }

    initSet(name) {
        const remember = localStorage.rememberFilters === 'true';
        if (!remember) return new Set();

        const stored = localStorage.getItem(name);
        if (!stored) return new Set();

        return new Set(JSON.parse(stored));
    }

    resetFilters = () => {
        this.changeFilter(null);

        if (!this.state.showActive) this.toggleActive();
        if (!this.state.showNotFound) this.toggleNotFound();
        if (this.state.showHidden) this.toggleHidden();

        // regular filters (easy)
        const filters = [ 'hideFactions', 'hideRaces', 'hideClasses', 'hideProfessions', 'hideRealms', 'hideAccounts' ];
        filters.forEach(filter => this.toggleFilter(filter, 'All'));

        this.changeLevelMin('');
        this.changeLevelMax('');
        this.changeItemLevelMin('');
        this.changeItemLevelMax('');
    }

    toggleActive = () => {
        localStorage.setItem('showActive', this.state.showActive ? 'false' : 'true');
        this.setState({ showActive: !this.state.showActive });
        this.updateLists();
    }

    toggleNotFound = () => {
        localStorage.setItem('showNotFound', this.state.showNotFound ? 'false' : 'true');
        this.setState({ showNotFound: !this.state.showNotFound });
        this.updateLists();
    }

    toggleHidden = () => {
        localStorage.setItem('showHidden', this.state.showHidden ? 'false' : 'true');
        this.setState({ showHidden: !this.state.showHidden });
        this.updateLists();
    }

    toggleFactions = () => {
        this.modifyDrawer('showFactions');
    }

    toggleRaces = () => {
        this.modifyDrawer('showRaces');
    }

    toggleClasses = () => {
        this.modifyDrawer('showClasses');
    }

    toggleProfessions = () => {
        this.modifyDrawer('showProfessions');
    }

    toggleLevels = () => {
        this.modifyDrawer('showLevels');
    }

    toggleItemLevels = () => {
        this.modifyDrawer('showItemLevels');
    }

    toggleRealms = () => {
        this.modifyDrawer('showRealms');
    }

    toggleAccounts = () => {
        this.modifyDrawer('showAccounts');
    }

    toggleFilters = (event) => {
        const showFilters = this.state.showFilters ? false : event.currentTarget;
        this.setState({ showFilters });
    }

    toggleSort = (event) => {
        const showSort = this.state.showSort ? false : event.currentTarget;
        this.setState({ showSort });
    }

    toggleRememberFilter = () => {
        if (localStorage.rememberFilters === 'true') {
            localStorage.removeItem('rememberFilters');
        } else {
            localStorage.setItem('rememberFilters', 'true');
        }
        this.setState({ rememberFilters: Date.now() }); // just to re-render
    }

    toggleFilter = (property, arg1, arg2) => {
        const hide = new Set(this.state[property]);
        if (arg1 === 'All') {
            hide.clear();
        } else if (arg1 === 'None') {
            for (let p of arg2) {
                hide.add(p);
            }
        } else {
            for (let s of Array.isArray(arg1) ? arg1 : [ arg1 ]) {
                hide.has(s) ? hide.delete(s) : hide.add(s);
            }
        }

        localStorage.setItem('favfilter.' + property, JSON.stringify(Array.from(hide)));
        this.setState({ [property]: hide });
        this.updateLists();
    }

    sort = (type, sort) => {
        localStorage.setItem('favsort', sort);
        this.setState({ sort });
        this.updateLists();
    }

    async loadUser(token) {
        // validate user token
        if (!token?.region || !token?.expires_at) {
            this.setState({ error: true, details: 'Invalid token\nPOST Info\n' + localStorage.getItem('token.blizzard') });
            return false;
        } else if (token.expires_at < Date.now()) {
            // expired token
            this.props.history.push('/redirect?to=blizzard&from=' + encodeURIComponent('/mycharacters') + '&region=' + encodeURIComponent(token.region));
            return false;
        }

        // Blizzard API call
        const response = await api.post('user/' + encodeURIComponent(token.region), token);
        if (response.status !== 200) {
            this.setState({ error: true, details: 'HTTP Error ' + response.status + '\nPOST Info\n' + localStorage.getItem('token.blizzard') });
            return false;
        }

        const json = await response.json();
        if (!json?.characters?.length) {
            this.setState({ error: true, details: 'Invalid JSON Response\nPOST Info\n' + localStorage.getItem('token.blizzard') });
            return false;
        }

        // load stubs from the database
        const response2 = await api.post('stubs', json.characters);
        if (response2.status !== 200) {
            this.setState({ error: true, details: 'HTTP Error ' + response2.status + '\nPOST Info\n' + localStorage.getItem('token.blizzard') });
            return false;
        }

        const json2 = await response2.json();
        if (!Array.isArray(json2)) {
            this.setState({ error: true, details: 'Invalid JSON Response\nPOST Info\n' + localStorage.getItem('token.blizzard') });
            return false;
        }

        return {
            id: json.id,
            api: json.characters.map(c => ({ character: { ...c, name: c.character }})),
            characters: json2.filter(c => c?.character?.key)
        };
    }

    async loadAll() {
        // reset state first
        this.setState(this.blankstate);

        let favorites = null;
        let favoritesJson = null;

        if (this.props.from === 'favorites') {
            favorites = favorite.get('favorites').getFavorites();
            const response = await api.post('stubs', favorites.map(c => ({ region: c.region, realm: c.realm, character: c.character })));
            if (response.status !== 200) {
                this.setState({ error: true });
                return;
            }

            favoritesJson = await response.json();
            this.setState({ favkey: 'favorites' });
        } else if (this.props.from === 'oauth') {
            let token = null;
            try {
                token = JSON.parse(localStorage.getItem('token.blizzard'));
            } catch (e) {

            }

            const json = await this.loadUser(token);
            if (!json) return; // error message already set

            // update login info
            const main = (json.characters[0] || json.api[0]);
            token.id = json.id;
            token.character = utils.clean(main.character?.region) + '#' + utils.slug(main.character?.realm) + '#' + utils.clean(main.character?.name);
            token.account = main.account?.id || undefined;
            localStorage.setItem('token.blizzard', JSON.stringify(token));

            // add any missing characters to the end
            const login = favorite.get('favorites.login' + token.id, 99999);
            const found = new Set();
            for (let stub of [ ...json.characters, ...json.api ]) {
                found.add(stub.character.region + '#' + stub.character.realm + '#' + stub.character.name);
                if (login.isFavorite(stub.character.region, stub.character.realm, stub.character.name)) {
                    login.updateFavorite(stub.character.region, stub.character.realm, stub.character.name, stub.character.class);
                } else {
                    login.addFavorite(stub.character.region, stub.character.realm, stub.character.name, stub.character.class);
                }
            }

            for (let fav of login.getFavorites()) {
                if (!found.has(fav.region + '#' + fav.realm + '#' + fav.character)) {
                    login.removeFavorite(fav.region, fav.realm, fav.character);
                }
            }

            this.setState({ favkey: 'favorites.login' + token.id, character: token.character, account: token.account });
            favorites = login.getFavorites();

            // copy wowaccount from the original API call into the character's stub data
            favoritesJson = json.characters.map(c => ({ ...c, wowaccount: json?.api?.find(api => api?.character?.key === c?.character?.key)?.character?.wowaccount }));
        }

        // after different HTTP calls are done, load it into state
        if (favorites !== null) {
            const wowaccounts = new Set();
            const realms = new Set();
            const state = { favorites, main: 0 };

            for (let character of favorites) {
                const key = utils.clean(character.region) + '#' + utils.slug(character.realm) + '#' + utils.clean(character.character);
                const child = favoritesJson.find(j => j.character?.key === key);
                state[character.region + '#' + character.realm + '#' + character.character + '#json'] = child || { message: 'Character requires update' };

                state.main = Math.max(state.main, child?.character?.main || 0);
                realms.add(character.realm);
                if (child?.wowaccount) wowaccounts.add(child?.wowaccount);
            }

            state.wowaccounts = Array.from(wowaccounts);
            state.wowaccounts.sort((a, b) => utils.collator.compare(accountName(a), accountName(b)));

            state.realms = Array.from(realms);
            state.realms.sort((a, b) => utils.collator.compare(a, b));

            this.setState(state);
            this.updateLists();
        }
    }

    load = async (region, realm, character, refresh) => {
        const response = await api.post('stubs', [{ region, realm, character }]);
        if (response.status !== 200) return;

        const json = await response.json();
        const key = region + '#' + realm + '#' + character + '#json';
        const value = json[0] || { message: 'Character requires update' };

        if (this.state[key]?.character?.updated === value.character?.updated) {
            // refresh failed
            if (refresh) value.updatefailed = true;
        }

        if (this.state[key]?.wowaccount) {
            // carry over wowaccount
            value.wowaccount = this.state[key].wowaccount;
        }

        this.setState(state => ({ [key]: value, main: Math.max(state.main, value?.character?.main || 0) }));
        this.updateLists();
    }

    changeFilter = (event) => {
        const filter = event?.currentTarget?.value || '';
        const regex = utils.regex(filter);
        this.setState({ filter, regex });
        this.updateLists();
    }

    changeLevelMin = (levelMin) => {
        localStorage.setItem('favfilter.levelMin', levelMin);
        this.setState({ levelMin });
        this.updateLists();
    }

    changeLevelMax = (levelMax) => {
        localStorage.setItem('favfilter.levelMax', levelMax);
        this.setState({ levelMax });
        this.updateLists();
    }

    changeItemLevelMin = (itemLevelMin) => {
        localStorage.setItem('favfilter.itemLevelMin', itemLevelMin);
        this.setState({ itemLevelMin });
        this.updateLists();
    }

    changeItemLevelMax = (itemLevelMax) => {
        localStorage.setItem('favfilter.itemLevelMax', itemLevelMax);
        this.setState({ itemLevelMax });
        this.updateLists();
    }

    // updates various derived state based on character list, including filtering and sorting
    updateLists = () => {
        this.setState(state => {
            if (!state.favorites) return null; // still loading

            // sort the list of characters
            const sortedCharacters = state.favorites.map((c, idx) => {
                const details = state[c.region + '#' + c.realm + '#' + c.character + '#json'];
                const fulltext = (c.region || '') + '-' + (c.realm || '') + '-' + (c.character || '') + ' ' + (details?.character?.guildName || '');
                return { ...c, idx, details, fulltext };
            });

            if (CHARSORTS[state.sort]) sortedCharacters.sort(CHARSORTS[state.sort]);

            // then filter the list of characters
            const gather = Object.values(FILTERS).filter(f => f.include(state)).map(f => f.eval);
            const filteredCharacters = sortedCharacters.filter(c => gather.every(fn => fn(state, c)));

            return { sortedCharacters, filteredCharacters };
        });
    }

    reorder = (favorites) => {
        this.setState({ favorites });
        this.updateLists();
    }

    refreshAll = async (event) => {
        if (event) event.preventDefault(); // prevent form submission
        if (this.state.loadingAll) return;

        this.setState({ loadingAll: true });

        try {
            const characters = [ ...this.state.filteredCharacters ];
            const response = await api.post('queue/add/multiple?jwt=' + encodeURIComponent(login.jwt.get()), characters);
            const tokens = await response.json();

            // indicates that characters have been safely added to queue
            if (tokens.length) this.setState({ loadingAll: tokens.length });

            for (let i = 0; i < characters.length; i++) {
                const character = characters[i];
                this.setState({ [character.region + '#' + character.realm + '#' + character.character]: true });

                try {
                    await this.queueWait(character.region, character.realm, character.character, tokens[i]);
                    await this.load(character.region, character.realm, character.character, true);
                } catch (e) {

                }

                this.setState({ [character.region + '#' + character.realm + '#' + character.character]: false });
            }
        } catch (e) {

        }

        this.setState({ loadingAll: false });
        return false;
    }

    queueWait = async (region, realm, name, token) => {
        let position = 0;
        while (true) {
            await utils.sleep(position ? 15000 : 1000);

            const response = await api.get('queue/status/' + encodeURIComponent(token));
            const json = await response.json();
            if (json.status === 'DONE') return true;

            position = json.position || 1;
            this.setState({ [region + '#' + realm + '#' + name]: json.position || true });
        }
    }

    lockout = () => {
        this.setState({ loadingOne: true });
    }

    refresh = async (region, realm, character) => {
        if (region && realm && character) await this.load(region, realm, character, true);
        this.setState({ loadingOne: false });
    }

    main = async (region, realm, character) => {
        const endpoint = 'main/' + encodeURIComponent(region) + '/' + encodeURIComponent(realm) + '/' + encodeURIComponent(character) + '?jwt=' + encodeURIComponent(login.jwt.get());
        const response = await api.post(endpoint, { main: true });
        if (response.status !== 204) {
            const key = region + '#' + realm + '#' + character + '#json';
            this.setState(state => ({ [key]: { ...state[key], updatefailed: true } }));
            return;
        }

        await this.load(region, realm, character, false);
    }

    hide = (region, realm, character, hidden) => {
        const store = favorite.get(this.state.favkey);
        store.updateFavorite(region, realm, character, null, null, hidden);
        this.setState({ favorites: store.getFavorites() });
        this.updateLists();
    }

    exclusive = async (event) => {
        if (event) event.preventDefault(); // prevent form submission
        if (this.state.loadingExclusive) return;

        const parts = this.state.character?.split('#');
        if (parts?.length !== 3) return; // TODO should give an error message

        this.setState({ loadingExclusive: true });

        try {
            const endpoint = 'exclusive/' + encodeURIComponent(parts[0]) + '/' + encodeURIComponent(parts[1]) + '/' + encodeURIComponent(parts[2]);
            const response = await api.get(endpoint);
            if (response.status !== 200) return; // TODO should give an error message

            const json = await response.json();
            this.setState({ exclusive: json });
        } finally {
            this.setState({ loadingExclusive: false });
        }
    }

    download = () => {
        let array = this.state.filteredCharacters;
        array = array.map(c => this.state[c.region + '#' + c.realm + '#' + c.character + '#json']);
        download.characters(array);
    }

    accountButton = (id) => {
        return <Tooltip title={<>Rename account #{id}</>}><span style={{ cursor: 'pointer' }} data-account={id} onClick={this.accountButtonClick}>{accountName(id)}</span></Tooltip>;
    }

    accountButtonClick = (event) => {
        const id = event.currentTarget.getAttribute('data-account');
        if (!id) return; // shouldn't happen

        const rename = prompt('Rename account:');
        if (!rename && rename !== '') return; // cancel

        if (rename === '') {
            localStorage.removeItem('rename.' + id);
        } else {
            localStorage.setItem('rename.' + id, rename);
        }

        this.setState({ renamed: Date.now() }); // trigger an update
        this.updateLists();
    }

    guildButton = (region, realm, guild) => {
        if (this.props.from === 'oauth') {
            return <Tooltip title="Import Guild Roster"><span style={{ cursor: 'pointer' }} data-region={region} data-realm={realm} data-guild={guild} onClick={this.guildButtonClick}>{guild}</span></Tooltip>;
        } else {
            return guild;
        }
    }

    guildButtonClick = (event) => {
        const region = event.currentTarget.getAttribute('data-region');
        const realm = event.currentTarget.getAttribute('data-realm');
        const name = event.currentTarget.getAttribute('data-guild');
        if (!name) return; // shouldn't happen

        this.setState({ guildModal: { region, realm, name } });
    }

    guildButtonClose = () => {
        this.setState({ guildModal: null });
    }

    portrait() {
        if (!this.state.favorites?.length) return null;

        // check if a main character is specified
        if (this.props.from === 'oauth') {
            for (let character of this.state.favorites) {
                const json = this.state[character.region + '#' + character.realm + '#' + character.character + '#json'];
                if ((this.state.main > 0) && (json?.character?.main === this.state.main)) {
                    return json.character;
                }
            }
        }

        // default to use the 1st character
        const first = this.state.favorites[0];
        return this.state[first?.region + '#' + first?.realm + '#' + first?.character + '#json']?.character || null;
    }

    renderCharacter = (character, idx) => {
        const filtered = this.state.filteredCharacters;
        const loading = this.state[character.region + '#' + character.realm + '#' + character.character];
        const json = this.state[character.region + '#' + character.realm + '#' + character.character + '#json'];

        const manual = !CHARSORTS[this.state.sort];
        const showUp = manual && (idx > 0);
        const showDown = manual && (idx < filtered.length - 1);

        const refresh = {
            message: json?.message,
            timestamp: json?.character?.updated
        };
        const main = {
            icon: farUser,
            tooltip: 'Set as main character'
        };

        if (json?.account?.exclude) {
            main.disabled = true;
            main.icon = faUserNinja;
            main.tooltip = 'This character is missing achievements used to detect alts';
            main.warning = main.tooltip;
        } else if (json?.message) {
            main.disabled = true;
            main.icon = faUserNinja;
            main.tooltip = json.message;
        } else if ((this.state.main > 0) && (json?.character?.main === this.state.main)) {
            main.disabled = true;
            main.icon = faUser;
            main.tooltip = 'Main character';
        } else if (this.state.account !== json.character.account) {
            // final condition, only allow main to be set if account matches
            main.disabled = true;
            main.icon = faUserNinja;
            main.tooltip = 'Not detected as an alt based on achievements';
            main.warning = main.tooltip;
        }

        if (json?.character && this.props.from === 'oauth' && this.state.account && this.state.account !== json.character.account) {
            refresh.warning = main.warning || 'Not detected as an alt based on achievements';
        }

        if (typeof loading === 'number') {
            refresh.message = '#' + loading + ' in queue';
        } else if (json.updatefailed) {
            refresh.message = 'Update Failed';
        }

        if (this.state.exclusive?.[json?.character?.key]) {
            refresh.exclusive = `This character appears to have collection items that others don't. Use the "Exclusive to This Character" advanced filter on the collection tabs for more details.`;
        }

        const link = '/characters/' + encodeURIComponent(character.region) + '/' + encodeURIComponent(character.realm) + '/' + encodeURIComponent(character.character);
        const jcp = Object.keys(json?.character?.professions || {}).map(key => ({ key, name: PROFESSIONS[key], primary: PRIMARIES[key], ...json.character.professions[key] }));
        jcp.sort((a, b) => utils.collator.compare(!a.primary + a.name, !b.primary + b.name));

        return (
            <>
                <div className="team-cell-move text-center">
                    {showUp ? <MoveButton direction="up" idx={character.idx} to={filtered[idx-1].idx} toMax={0} favkey={this.state.favkey} onChange={this.reorder} /> : null}
                    {showDown ? <MoveButton direction="down" idx={character.idx} to={filtered[idx+1].idx} toMax={this.state.favorites.length - 1} favkey={this.state.favkey} onChange={this.reorder} /> : null}
                </div>
                <div className="team-cell-avatar m-auto">
                    {json?.character?.thumbnail ?
                        <Link to={link}><img className="rounded" style={{ width: '2.5rem', height: '2.5rem' }} src={'https://render.worldofwarcraft.com/' + json.character.region.toLowerCase() + '/character/' + json.character.thumbnail} alt="Avatar" loading="lazy" /></Link>
                    : null}
                </div>
                <div className="team-cell-name text-nowrap">
                    <div><Link className={'text-break class-' + (json?.character?.class || character.class)} to={link}>{character.character}</Link></div>
                    {json?.character ?
                        <div className="small">
                            Level {json.character.level} {RACESMAP[json.character.race]} | <Tooltip title={'Item Level ' + json.character.averageItemLevel}><span><FontAwesomeIcon icon={faGavel} /> {json.character.averageItemLevel}</span></Tooltip>
                        </div>
                    : null}
                </div>
                {jcp?.some(p => p.primary) ?
                    <div className="team-cell-professions text-nowrap">
                        <Tooltip title={<table><tbody>{jcp.map(p => <tr key={p.key}><td>{p.name} {p.specialization ? '(' + p.specialization + ')' : null}</td><td className="pl-2 text-right">{p.skill}</td></tr>)}</tbody></table>}>
                            <div>
                                {jcp.filter(p => p.primary).map(p => <div key={p.key} className="small">{p.name}</div>)}
                            </div>
                        </Tooltip>
                    </div>
                : null}
                {json?.character?.guildName ? <div className="team-cell-guild text-break">{this.guildButton(json?.character?.region, json?.character?.guildRealm, json?.character?.guildName)}</div> : null}
                <div className="team-cell-realm text-nowrap">{character.realm}{this.state.wowaccounts?.length > 1 ? <div>{this.accountButton(json?.wowaccount)}</div> : null}</div>
                <div className="team-cell-buttons">
                    <div className="d-flex justify-content-end">
                        <div><RefreshButton loading={loading} waiting={this.state.loadingOne || this.state.loadingAll} region={character.region} realm={character.realm} character={character.character} message={refresh.message} warning={refresh.warning} exclusive={refresh.exclusive} timestamp={refresh.timestamp} onClick={this.lockout} onRefresh={this.refresh} /></div>
                        {this.props.from === 'oauth' ? <div className="pl-1"><MainButton disabled={main.disabled} tooltip={main.tooltip} icon={main.icon} region={character.region} realm={character.realm} character={character.character} onClick={this.main} /></div> : null}
                        {this.props.from === 'oauth' ? <div className="pl-1"><HideButton region={character.region} realm={character.realm} character={character.character} hidden={character.hidden} onClick={this.hide} /></div> : null}
                        <div className="pl-1"><FavoriteButton region={character.region} realm={character.realm} name={character.character} class={json?.character?.class} thumbnail={json?.character?.thumbnail} /></div>
                    </div>
                </div>
            </>
        );
    }

    render() {
        const title = this.props.from === 'oauth' ? 'My Characters' : 'Favorite Characters';
        document.title = title + ' | Data for Azeroth | World of Warcraft Leaderboards for Collectors';

        const table = [];
        const leaderboards = [];

        if (this.state.error) {
            return <CharactersError details={this.state.details} />;
        } else if (this.state.favorites === null) {
            table.push(
                <div key="loading" className="my-auto text-center">
                    <FontAwesomeIcon icon={faCog} spin />
                </div>
            );
        } else if (!this.state.favorites?.length) {
            table.push(
                <div key="info" className="my-auto pl-3">
                    Please choose your favorite characters by clicking the <FontAwesomeIcon icon={farStar} /> button on their profile.
                </div>
            );
        } else if (!this.state.filteredCharacters?.length) {
            table.push(
                <div key="info" className="my-auto pl-3">
                    No matches found
                </div>
            );
        } else {
            const filtered = this.state.filteredCharacters;

            for (let i = 0; i < filtered.length; i++) {
                const character = filtered[i];
                leaderboards.push(utils.clean(character.region) + '#' + utils.slug(character.realm) + '#' + utils.clean(character.character));
                table.push(<RenderIfVisible key={character.idx} render={() => this.renderCharacter(character, i)} className="team-row" style={{ gridRow: i+2 }} />);
            }
        }

        const portrait = this.portrait();
        const count = this.state.filteredCharacters?.length || 0;
        const allFiltersInDefaultState = !this.state.hideFactions.size && !this.state.hideRaces.size && !this.state.hideClasses.size && !this.state.hideProfessions.size && !this.state.levelMin && !this.state.levelMax && !this.state.itemLevelMin && !this.state.itemLevelMax && !this.state.hideRealms.size && !this.state.hideAccounts.size;

        return (
            <div>
                <Section title={title} subtitle={count + ' Character' + (count === 1 ? '' : 's') + ' Shown'} icon={'https://render.worldofwarcraft.com/' + (portrait ? portrait.region.toLowerCase() + '/character/' + portrait.thumbnail : 'us/icons/56/inv_helmet_30.jpg') }>
                    <div className="d-flex flex-wrap">
                        <div className={'btn-group' + (window.bootstrap.md.matches ? ' mb-3 mr-3' : '-vertical w-100 mb-3')  + ' flex-wrap position-relative'}>
                            <input type="text" placeholder="Search" className={'form-control pr-4' + (window.bootstrap.md.matches ? '' : ' flex-fill')} value={this.state.filter} onChange={this.changeFilter} />
                            <button type="button" title="Clear" className="btn btn-clear" onClick={this.changeFilter}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div className={'btn-group' + (window.bootstrap.md.matches ? ' mb-3 mr-3' : '-vertical w-100 mb-3')  + ' flex-wrap'}>
                            <ToggleButton label="Active" checked={this.state.showActive} onToggle={this.toggleActive} />
                            <ToggleButton label="Missing" checked={this.state.showNotFound} onToggle={this.toggleNotFound} />
                            <ToggleButton label="Hidden" checked={this.state.showHidden} onToggle={this.toggleHidden} visible={this.props.from === 'oauth'} />
                        </div>
                        <div className={'btn-group' + (window.bootstrap.md.matches ? ' mb-3 mr-3' : '-vertical w-100 mb-3')  + ' flex-wrap'}>
                            <CollapseButton label={allFiltersInDefaultState ? 'Filter' : <><FontAwesomeIcon icon={faCheckCircle} /> Filter</>} open={this.state.showFilters} onToggle={this.toggleFilters} />
                            <CollapseButton label="Sort" open={this.state.showSort} onToggle={this.toggleSort} />
                            <Tooltip title="Reset All Filters">
                                <button type="button" className="btn btn-primary" onClick={this.resetFilters}><FontAwesomeIcon icon={faUndo} /></button>
                            </Tooltip>
                        </div>
                        <div className={'btn-group' + (window.bootstrap.md.matches ? ' mb-3 mr-3' : '-vertical w-100 mb-3')  + ' flex-wrap'}>
                            <Tooltip title="Download CSV File"><button type="button" className="btn btn-primary" onClick={this.download}><FontAwesomeIcon icon={faFileDownload} /></button></Tooltip>
                        </div>

                        {this.props.from === 'favorites' ?
                            <div className={'btn-group' + (window.bootstrap.md.matches ? ' mb-3 mr-3' : '-vertical w-100 mb-3')  + ' flex-wrap'}>
                                <Link className="btn btn-primary" to={'/leaderboards/completion-score/custom/characters/' + encodeURIComponent(utils.pack(leaderboards))}>Leaderboards</Link>
                            </div>
                        : null}
                        {this.props.from === 'oauth' && this.state.character && !this.state.exclusive ?
                            <div className={'btn-group' + (window.bootstrap.md.matches ? ' mb-3 mr-3' : '-vertical w-100 mb-3')  + ' flex-wrap'}>
                                <Tooltip title="Analyzes your characters and displays an icon if they have collection items that no other character has. May take a few moments to run and results won't be 100% accurate due to limitations of Blizzard's API. Use at your own risk.">
                                    <form method="post" onSubmit={this.exclusive} className={window.bootstrap.md.matches ? null : 'w-100'}>
                                        <button type="submit" className={'btn btn-primary text-nowrap' + (window.bootstrap.md.matches ? '' : ' w-100') + (this.state.loadingExclusive ? ' disabled' : '')}>Exclusive</button>
                                    </form>
                                </Tooltip>
                            </div>
                        : null}
                        {this.state.favorites ?
                            <div className={window.bootstrap.md.matches ? 'ml-auto mb-3' : 'w-100 mb-3'}>
                                <Tooltip title="Update all characters from Warcraft armory">
                                    <form method="post" onSubmit={this.refreshAll} className={window.bootstrap.md.matches ? null : 'w-100'}>
                                        <button type="submit" className={'btn btn-primary text-nowrap' + (window.bootstrap.md.matches ? '' : ' w-100') + (this.state.loadingAll ? ' disabled' : '')}><FontAwesomeIcon icon={faSync} spin={this.state.loadingAll} /> Update All</button>
                                    </form>
                                </Tooltip>
                            </div>
                        : null}
                    </div>

                    {this.props.from === 'oauth' ?
                        <>
                        <div className={'btn-group' + (window.bootstrap.md.matches ? ' mr-3' : '-vertical w-100 mb-3')  + ' flex-wrap'}>
                            <Link to="/rewards" className="btn btn-patreon text-nowrap">
                                <FontAwesomeIcon icon={faRibbon} className="mr-2" />
                                <FontAwesomeIcon icon={faPatreon} className="mr-2" />
                                <FontAwesomeIcon icon={faTwitch} className="mr-2" />
                                Claim Supporter Rewards
                            </Link>
                        </div>

                        {(window.location.hostname === 'dev.dataforazeroth.com') ?
                            <div className={'btn-group' + (window.bootstrap.md.matches ? ' mr-3' : '-vertical w-100 mb-3')  + ' flex-wrap'}>
                                <Link to={'/redirect?to=twitch&with=scope&from=/debug'} className="btn btn-twitch text-nowrap"><FontAwesomeIcon icon={faTwitch} className="mr-2" />Refresh Token</Link>
                            </div>
                        : null}

                        <div className={'btn-group' + (window.bootstrap.md.matches ? ' mr-3' : '-vertical w-100 mb-3')  + ' flex-wrap'}>
                            <BlockUpdateButton />
                            <LanguageDown />
                        </div>
                        </>
                    : null}

                    <Popper open={this.state.showFilters} anchorEl={this.state.showFilters} placement="bottom-start">
                        <ClickAwayListener onClickAway={this.toggleFilters}>
                            <div className="card mt-1">
                                <div className="btn-group-vertical scroller">
                                    <FilterButton label="Faction" open={this.state.showFactions} default={!this.state.hideFactions.size} onToggle={this.toggleFactions} />
                                    <FilterButton label="Race" open={this.state.showRaces} default={!this.state.hideRaces.size} onToggle={this.toggleRaces} />
                                    <FilterButton label="Class" open={this.state.showClasses} default={!this.state.hideClasses.size} onToggle={this.toggleClasses} />
                                    <FilterButton label="Profession" open={this.state.showProfessions} default={!this.state.hideProfessions.size} onToggle={this.toggleProfessions} />
                                    <FilterButton label="Level" open={this.state.showLevels} default={!this.state.levelMin && !this.state.levelMax} onToggle={this.toggleLevels} />
                                    <FilterButton label="Item Level" open={this.state.showItemLevels} default={!this.state.itemLevelMin && !this.state.itemLevelMax} onToggle={this.toggleItemLevels} />
                                    <FilterButton label="Realm" open={this.state.showRealms} default={!this.state.hideRealms.size} onToggle={this.toggleRealms} visible={this.state.realms?.length > 1} />
                                    <FilterButton label="Account" open={this.state.showAccounts} default={!this.state.hideAccounts.size} onToggle={this.toggleAccounts} visible={this.state.wowaccounts?.length > 1} />

                                    <button type="button" className={localStorage.rememberFilters ? 'btn btn-primary active' : 'btn btn-primary'} onClick={this.toggleRememberFilter}><FontAwesomeIcon icon={localStorage.rememberFilters ? faCheckCircle : faCircle} /> Remember Filters</button>
                                </div>
                            </div>
                        </ClickAwayListener>
                    </Popper>
                    {this.state.showFactions ?
                        <>
                        <div className="small mt-3">Faction</div>
                        <div className={'btn-group' + (window.bootstrap.md.matches ? '' : '-vertical w-100')  + ' flex-wrap mt-1 mr-3'}>
                            <CollapseButton onToggle={this.toggleFactions} />
                            <ToggleButtonWrapper id="" label="Unknown" property="hideFactions" checked={!this.state.hideFactions.has('')} onToggle={this.toggleFilter} />
                            <ToggleButtonWrapper id={0} label="Alliance" property="hideFactions" checked={!this.state.hideFactions.has(0)} onToggle={this.toggleFilter} />
                            <ToggleButtonWrapper id={1} label="Horde" property="hideFactions" checked={!this.state.hideFactions.has(1)} onToggle={this.toggleFilter} />
                        </div>
                        </>
                    : null}
                    {this.state.showRaces ?
                        <>
                        <div className="small mt-3">Race</div>
                        <div className={'btn-group' + (window.bootstrap.md.matches ? '' : '-vertical w-100')  + ' flex-wrap mt-1 mr-3'}>
                            <CollapseButton onToggle={this.toggleRaces} />
                            <ToggleButtonWrapper id="All" property="hideRaces" variants={RACES.map(c => c.key)} onToggle={this.toggleFilter} />
                            <ToggleButtonWrapper id="None" property="hideRaces" variants={RACES.map(c => c.key)} onToggle={this.toggleFilter} />
                            {RACES.map(c => <ToggleButtonWrapper key={c.key} id={c.key} label={c.value} property="hideRaces" checked={!this.state.hideRaces.has(c.key)} onToggle={this.toggleFilter} />)}
                        </div>
                        </>
                    : null}
                    {this.state.showClasses ?
                        <>
                        <div className="small mt-3">Class</div>
                        <div className={'btn-group' + (window.bootstrap.md.matches ? '' : '-vertical w-100')  + ' flex-wrap mt-1 mr-3'}>
                            <CollapseButton onToggle={this.toggleClasses} />
                            <ToggleButtonWrapper id="All" property="hideClasses" variants={CLASSES.map(c => c.key)} onToggle={this.toggleFilter} />
                            <ToggleButtonWrapper id="None" property="hideClasses" variants={CLASSES.map(c => c.key)} onToggle={this.toggleFilter} />
                            {CLASSES.map(c => <ToggleButtonWrapper key={c.key} id={c.key} label={c.value} property="hideClasses" checked={!this.state.hideClasses.has(c.key)} onToggle={this.toggleFilter} />)}
                        </div>
                        </>
                    : null}
                    {this.state.showProfessions ?
                        <>
                        <div className="small mt-3">Profession</div>
                        <div className={'btn-group' + (window.bootstrap.md.matches ? '' : '-vertical w-100')  + ' flex-wrap mt-1 mr-3'}>
                            <CollapseButton onToggle={this.toggleProfessions} />
                            <ToggleButtonWrapper id="All" property="hideProfessions" variants={PROFFILTERS.map(p => p.key)} onToggle={this.toggleFilter} />
                            <ToggleButtonWrapper id="None" property="hideProfessions" variants={PROFFILTERS.map(p => p.key)} onToggle={this.toggleFilter} />
                            {PROFFILTERS.map(p => <ToggleButtonWrapper key={p.key} id={p.key} label={p.value} property="hideProfessions" checked={!this.state.hideProfessions.has(p.key)} onToggle={this.toggleFilter} />)}
                        </div>
                        </>
                    : null}
                    {this.state.showLevels ?
                        <>
                        <div className="small mt-3">Level</div>
                        <div className="mt-1">
                            <div className={'btn-group' + (window.bootstrap.md.matches ? '' : '-vertical w-100 mb-3')  + ' flex-wrap mr-3'}>
                                <CollapseButton onToggle={this.toggleLevels} />
                            </div>
                            <div className="btn-group mr-3">
                                <div className="my-auto"><NumberInput placeholder="Min" className="form-control" value={this.state.levelMin} onChange={this.changeLevelMin} /></div>
                                <div className="my-auto mx-1">-</div>
                                <div className="my-auto"><NumberInput placeholder="Max" className="form-control" value={this.state.levelMax} onChange={this.changeLevelMax} /></div>
                            </div>
                        </div>
                        </>
                    : null}
                    {this.state.showItemLevels ?
                        <>
                        <div className="small mt-3">Item Level</div>
                        <div className="mt-1">
                            <div className={'btn-group' + (window.bootstrap.md.matches ? '' : '-vertical w-100 mb-3')  + ' flex-wrap mr-3'}>
                                <CollapseButton onToggle={this.toggleItemLevels} />
                            </div>
                            <div className="btn-group mr-3">
                                <div className="my-auto"><NumberInput placeholder="Min" className="form-control" value={this.state.itemLevelMin} onChange={this.changeItemLevelMin} /></div>
                                <div className="my-auto mx-1">-</div>
                                <div className="my-auto"><NumberInput placeholder="Max" className="form-control" value={this.state.itemLevelMax} onChange={this.changeItemLevelMax} /></div>
                            </div>
                        </div>
                        </>
                    : null}
                    {this.state.showRealms ?
                        <>
                        <div className="small mt-3">Realm</div>
                        <div className={'btn-group' + (window.bootstrap.md.matches ? '' : '-vertical w-100')  + ' flex-wrap mt-1 mr-3'}>
                            <CollapseButton onToggle={this.toggleRealms} />
                            <ToggleButtonWrapper id="All" property="hideRealms" variants={[ 0, ...(this.state.realms || []) ]} onToggle={this.toggleFilter} />
                            <ToggleButtonWrapper id="None" property="hideRealms" variants={[ 0, ...(this.state.realms || []) ]} onToggle={this.toggleFilter} />
                            <ToggleButtonWrapper key={0} id={0} label="Unknown" property="hideRealms" checked={!this.state.hideRealms.has(0)} onToggle={this.toggleFilter} />
                            {this.state.realms?.map(a => <ToggleButtonWrapper key={a} id={a} label={a} property="hideRealms" checked={!this.state.hideRealms.has(a)} onToggle={this.toggleFilter} />)}
                        </div>
                        </>
                    : null}
                    {this.state.showAccounts ?
                        <>
                        <div className="small mt-3">Account</div>
                        <div className={'btn-group' + (window.bootstrap.md.matches ? '' : '-vertical w-100')  + ' flex-wrap mt-1 mr-3'}>
                            <CollapseButton onToggle={this.toggleAccounts} />
                            <ToggleButtonWrapper id="All" property="hideAccounts" variants={[ 0, ...(this.state.wowaccounts || []) ]} onToggle={this.toggleFilter} />
                            <ToggleButtonWrapper id="None" property="hideAccounts" variants={[ 0, ...(this.state.wowaccounts || []) ]} onToggle={this.toggleFilter} />
                            <ToggleButtonWrapper key={0} id={0} label="Unknown" property="hideAccounts" checked={!this.state.hideAccounts.has(0)} onToggle={this.toggleFilter} />
                            {this.state.wowaccounts?.map(a => <ToggleButtonWrapper key={a} id={a} label={accountName(a)} property="hideAccounts" checked={!this.state.hideAccounts.has(a)} onToggle={this.toggleFilter} />)}
                        </div>
                        </>
                    : null}
                    <Popper open={this.state.showSort} anchorEl={this.state.showSort} placement="bottom-start">
                        <ClickAwayListener onClickAway={this.toggleSort}>
                            <div className="card mt-1">
                                <div className="btn-group-vertical scroller">
                                    {['manual', 'realm', 'guild', 'name', 'level', 'wowaccount'].map(s => <SortButton key={s} label={SORTNAMES[s]} sort={s} active={this.state.sort} onToggle={this.sort} />)}
                                </div>
                            </div>
                        </ClickAwayListener>
                    </Popper>
                    {typeof this.state.loadingAll === 'number' ? <div className="mt-3 p-2 rounded rankings"><FontAwesomeIcon icon={faInfoCircle} /> {this.state.loadingAll} characters have been added to the queue. You can leave this page and the server will continue to process your updates.</div> : null}
                </Section>

                <div className="m-3 card">
                    <div className="team-grid">
                        <div className="team-row team-header text-nowrap">
                            <div className="my-auto" style={{ gridColumn: 3 }}>Character</div>
                            {window.bootstrap.md.matches ? <>
                                <div className="my-auto" style={{ gridColumn: 4 }}>Professions</div>
                                <div className="my-auto" style={{ gridColumn: 5 }}>Guild</div>
                                <div className="my-auto" style={{ gridColumn: 6 }}>Realm</div>
                            </> : null}
                        </div>

                        {table}
                    </div>
                </div>

                <GuildModal guild={this.state.guildModal} onClose={this.guildButtonClose} />
            </div>
        );
    }
}